import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button, Card, Form, Input, Select, Space, Typography, Divider,
  Radio, InputNumber,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { createSurvey, Question, QuestionType } from '../api/surveys';
import { useAuth } from '../context/AuthContext';

const { Title } = Typography;

const Q_TYPES: { value: QuestionType; label: string }[] = [
  { value: 'single', label: 'Single choice' },
  { value: 'multiple', label: 'Multiple choice' },
  { value: 'rating', label: 'Rating (stars)' },
  { value: 'text', label: 'Open text' },
];

const CITIES = ['Bangkok', 'Chiang Mai', 'Phuket', 'Pattaya', 'Hua Hin', 'Other'];
const CATEGORIES = ['Transport', 'Food & Agriculture', 'Environment', 'Community', 'Health', 'Other'];

interface QForm {
  id: string;
  text: string;
  type: QuestionType;
  options: string[];
  maxRating: number;
  required: boolean;
}

function emptyQ(): QForm {
  return { id: crypto.randomUUID(), text: '', type: 'single', options: ['', ''], maxRating: 5, required: true };
}

export default function CreateSurvey() {
  const navigate = useNavigate();
  const { userEmail } = useAuth();
  const [form] = Form.useForm();
  const [questions, setQuestions] = useState<QForm[]>([emptyQ()]);

  function addQuestion() { setQuestions(qs => [...qs, emptyQ()]); }
  function removeQuestion(id: string) { setQuestions(qs => qs.filter(q => q.id !== id)); }
  function updateQ(id: string, update: Partial<QForm>) {
    setQuestions(qs => qs.map(q => q.id === id ? { ...q, ...update } : q));
  }
  function addOption(id: string) { updateQ(id, { options: [...(questions.find(q => q.id === id)?.options ?? []), ''] }); }
  function removeOption(id: string, idx: number) {
    const q = questions.find(q => q.id === id);
    if (!q) return;
    updateQ(id, { options: q.options.filter((_, i) => i !== idx) });
  }
  function setOption(id: string, idx: number, val: string) {
    const q = questions.find(q => q.id === id);
    if (!q) return;
    const opts = [...q.options];
    opts[idx] = val;
    updateQ(id, { options: opts });
  }

  function onFinish(values: { title: string; description: string; city: string; category: string }) {
    const qs: Question[] = questions.map(q => ({
      id: q.id,
      text: q.text,
      type: q.type,
      required: q.required,
      ...(q.type === 'rating' ? { maxRating: q.maxRating } : {}),
      ...(['single', 'multiple'].includes(q.type) ? { options: q.options.filter(o => o.trim() !== '') } : {}),
    }));
    const survey = createSurvey({
      ...values,
      questions: qs,
      createdBy: userEmail ?? 'Anonymous',
      closedAt: null,
    });
    navigate(`/results/${survey.id}`);
  }

  return (
    <div style={{ padding: 24, maxWidth: 760, margin: '0 auto' }}>
      <Title level={3} style={{ marginBottom: 24 }}>Create Survey</Title>

      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Card style={{ marginBottom: 16 }}>
          <Form.Item name="title" label="Survey Title" rules={[{ required: true }]}>
            <Input placeholder="e.g. Urban Mobility Feedback 2026" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="What is this survey about?" />
          </Form.Item>
          <Space>
            <Form.Item name="city" label="City" rules={[{ required: true }]}>
              <Select style={{ width: 180 }}>
                {CITIES.map(c => <Select.Option key={c} value={c}>{c}</Select.Option>)}
              </Select>
            </Form.Item>
            <Form.Item name="category" label="Category" rules={[{ required: true }]}>
              <Select style={{ width: 200 }}>
                {CATEGORIES.map(c => <Select.Option key={c} value={c}>{c}</Select.Option>)}
              </Select>
            </Form.Item>
          </Space>
        </Card>

        {questions.map((q, qi) => (
          <Card
            key={q.id}
            style={{ marginBottom: 12 }}
            title={<span>Question {qi + 1}</span>}
            extra={
              questions.length > 1 &&
              <Button danger icon={<DeleteOutlined />} size="small" type="text" onClick={() => removeQuestion(q.id)} />
            }
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Input
                placeholder="Question text"
                value={q.text}
                onChange={e => updateQ(q.id, { text: e.target.value })}
              />
              <Space>
                <Select value={q.type} onChange={v => updateQ(q.id, { type: v as QuestionType })} style={{ width: 180 }}>
                  {Q_TYPES.map(t => <Select.Option key={t.value} value={t.value}>{t.label}</Select.Option>)}
                </Select>
                <Radio.Group
                  value={q.required}
                  onChange={e => updateQ(q.id, { required: e.target.value as boolean })}
                  size="small"
                >
                  <Radio.Button value={true}>Required</Radio.Button>
                  <Radio.Button value={false}>Optional</Radio.Button>
                </Radio.Group>
                {q.type === 'rating' && (
                  <Space>
                    <span style={{ fontSize: 13 }}>Max stars:</span>
                    <InputNumber min={2} max={10} value={q.maxRating} onChange={v => updateQ(q.id, { maxRating: v ?? 5 })} style={{ width: 70 }} />
                  </Space>
                )}
              </Space>

              {['single', 'multiple'].includes(q.type) && (
                <div>
                  {q.options.map((opt, oi) => (
                    <div key={oi} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                      <Input
                        placeholder={`Option ${oi + 1}`}
                        value={opt}
                        onChange={e => setOption(q.id, oi, e.target.value)}
                        style={{ flex: 1 }}
                      />
                      {q.options.length > 2 && (
                        <Button icon={<DeleteOutlined />} size="small" type="text" danger onClick={() => removeOption(q.id, oi)} />
                      )}
                    </div>
                  ))}
                  <Button icon={<PlusOutlined />} size="small" type="dashed" onClick={() => addOption(q.id)}>Add option</Button>
                </div>
              )}
            </Space>
          </Card>
        ))}

        <Button icon={<PlusOutlined />} onClick={addQuestion} style={{ marginBottom: 20, borderStyle: 'dashed' }} block>
          Add Question
        </Button>

        <Divider />
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button onClick={() => navigate('/')}>Cancel</Button>
          <Button type="primary" htmlType="submit">Publish Survey</Button>
        </div>
      </Form>
    </div>
  );
}
